import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Badge,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  Menu,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Add,
  Notifications,
  AccountCircle,
  Tune,
  Language as LanguageIcon,
  FlashOn,
  Schedule,
  Settings,
  RecordVoiceOver,
  School,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDevice } from '../contexts/DeviceContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useWaitingDevices } from '../hooks/useWaitingDevices';

const CONTROLS_PATH = '/dashboard/controls';

/** The device selector never gets narrower than this on a phone (px) */
const PHONE_SELECTOR_MIN = 100;
/** Width the pending-invites bell takes in the app bar (px, measured) */
const INVITES_BELL_WIDTH = 60;

/** The "a device waits for your approval" dot (BA-58): warning orange with a white ring */
const WaitingDot: React.FC<{ show: boolean; label: string; mr?: number; sx?: object; children: React.ReactNode }> = ({
  show,
  label,
  mr,
  sx,
  children,
}) => (
  <Badge
    variant="dot"
    color="warning"
    invisible={!show}
    overlap="rectangular"
    slotProps={{ badge: { 'aria-label': show ? label : undefined, role: show ? 'status' : undefined } as object }}
    sx={{ mr, flexShrink: 1, minWidth: 0, ...sx, '& .MuiBadge-dot': { width: 10, height: 10, borderRadius: '50%', boxShadow: '0 0 0 2px #fff' } }}
  >
    {children}
  </Badge>
);

interface NavItem {
  key: string;
  path: string;
  labelKey: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { key: 'controls', path: CONTROLS_PATH, labelKey: 'tabs.controls', icon: <Tune /> },
  { key: 'websites', path: '/dashboard/websites', labelKey: 'tabs.websites', icon: <LanguageIcon /> },
  { key: 'actions', path: '/dashboard/actions', labelKey: 'tabs.actions', icon: <FlashOn /> },
  { key: 'schedules', path: '/dashboard/schedules', labelKey: 'tabs.schedules', icon: <Schedule /> },
  { key: 'talkingpal', path: '/dashboard/talkingpal', labelKey: 'tabs.talkingpal', icon: <RecordVoiceOver /> },
  { key: 'exercises', path: '/dashboard/exercises', labelKey: 'tabs.exercises', icon: <School /> },
  { key: 'settings', path: '/dashboard/settings', labelKey: 'tabs.settings', icon: <Settings /> },
];

const DashboardLayout: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const {
    devices,
    selectedDevice,
    setSelectedDevice,
    pendingInvites,
    snackbar,
    closeSnackbar,
    handleAddDevice,
    handleAcceptInvite,
  } = useDevice();
  // On a phone the title shows only where it fits whole beside a usable device selector
  // (no stub like "Co…"); narrower than that it is absent. The width is per language.
  // The pending-invites bell takes room too, so the threshold grows by its width.
  const titleMinWidth =
    Number(t('dashboard.titleShortMinWidth')) + (pendingInvites.length > 0 ? INVITES_BELL_WIDTH : 0);
  const titleFits = useMediaQuery(`(min-width:${titleMinWidth}px)`);
  const showTitle = !isPhone || titleFits;

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');

  const waitingIds = useWaitingDevices(devices.map((d) => d.id));
  const anyWaiting = waitingIds.length > 0;
  const waitingLabel =
    waitingIds.length === 1
      ? t('currentAction.waitingAria', { device: devices.find((d) => d.id === waitingIds[0])?.name ?? '' })
      : t('currentAction.waitingAriaMany', { count: waitingIds.length });

  // Tab title: "(N) Waiting for approval – …" while any device waits
  const baseTitleRef = useRef(document.title);
  useEffect(() => {
    const base = baseTitleRef.current;
    document.title = anyWaiting
      ? `${t('currentAction.tabTitle', { count: waitingIds.length })} – ${base}`
      : base;
  }, [anyWaiting, waitingIds.length, t]);
  useEffect(() => {
    const base = baseTitleRef.current;
    return () => {
      document.title = base;
    };
  }, []);

  // Opening Controls from another page leads to the one waiting device, so the dot ends
  // at its Approve. Two or more waiting, or already on Controls: nothing switches.
  const previousPathRef = useRef<string | null>(null);
  const waitingKey = waitingIds.join('|');
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;
    if (location.pathname !== CONTROLS_PATH || previousPath === CONTROLS_PATH) {
      return;
    }
    const ids = waitingKey ? waitingKey.split('|') : [];
    if (ids.length !== 1 || selectedDevice?.id === ids[0]) {
      return;
    }
    const device = devices.find((d) => d.id === ids[0]);
    if (device) setSelectedDevice(device);
    // Only on arriving at Controls: a device that starts waiting meanwhile doesn't move the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const handleAddDeviceSubmit = async () => {
    await handleAddDevice(newDeviceName);
    setNewDeviceName('');
    setOpenDialog('');
  };

  const drawerContent = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">{t('dashboard.title')}</Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActiveRoute(item.path)}
            >
              <ListItemIcon>
                {item.path === CONTROLS_PATH ? (
                  <WaitingDot show={anyWaiting} label={waitingLabel}>{item.icon}</WaitingDot>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar sx={{ px: { xs: 1.5, sm: 3 } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: isPhone ? 1 : 2 }}
            >
              <WaitingDot show={anyWaiting} label={waitingLabel}>
                <MenuIcon />
              </WaitingDot>
            </IconButton>
          )}

          {showTitle && (
            <Typography
              variant="h6"
              noWrap
              sx={{ flexGrow: isMobile ? 1 : 0, mr: isPhone ? 1 : isMobile ? 0 : 4, flexShrink: 0 }}
            >
              {t(isPhone ? 'dashboard.titleShort' : 'dashboard.title')}
            </Typography>
          )}

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.key}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  startIcon={item.icon}
                  sx={{
                    backgroundColor: isActiveRoute(item.path)
                      ? 'rgba(255, 255, 255, 0.15)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  }}
                >
                  {item.path === CONTROLS_PATH ? (
                    <WaitingDot show={anyWaiting} label={waitingLabel}>
                      {t(item.labelKey)}
                    </WaitingDot>
                  ) : (
                    t(item.labelKey)
                  )}
                </Button>
              ))}
            </Box>
          )}

          {pendingInvites.length > 0 && (
            <IconButton color="inherit" onClick={() => setOpenDialog('invites')} sx={isPhone ? { mr: 0, p: 0.5 } : { mr: 1 }}>
              <Notifications />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                {pendingInvites.length}
              </Typography>
            </IconButton>
          )}

          <WaitingDot
            show={anyWaiting}
            label={waitingLabel}
            mr={isPhone ? 1 : 2}
            sx={isPhone ? { minWidth: PHONE_SELECTOR_MIN, ml: showTitle ? 0 : 'auto' } : undefined}
          >
          <FormControl variant="outlined" size="small" sx={{ minWidth: isPhone ? PHONE_SELECTOR_MIN : isMobile ? 120 : 200 }}>
            <Select
              value={selectedDevice?.id || ''}
              onChange={(e) => {
                const device = devices.find(d => d.id === e.target.value);
                if (device) setSelectedDevice(device);
              }}
              displayEmpty
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '.MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="" disabled>
                {t('dashboard.selectDevice')}
              </MenuItem>
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name}
                  {waitingIds.includes(device.id) && (
                    <Box
                      component="span"
                      role="status"
                      aria-label={t('currentAction.waitingAria', { device: device.name })}
                      sx={{ ml: 1, width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </WaitingDot>

          <LanguageSwitcher />

          <IconButton
            color="inherit"
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          >
            <AccountCircle />
          </IconButton>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{currentUser?.email}</Typography>
            </MenuItem>
            <MenuItem onClick={() => {
              setUserMenuAnchor(null);
              setOpenDialog('addDevice');
            }}>
              <Add sx={{ mr: 1 }} />
              {t('dashboard.addDevice')}
            </MenuItem>
            <MenuItem onClick={() => {
              setUserMenuAnchor(null);
              handleLogout();
            }}>
              <Logout sx={{ mr: 1 }} />
              {t('dashboard.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      >
        {drawerContent}
      </Drawer>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {selectedDevice ? (
          <Outlet />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" align="center">
                {t('dashboard.noDeviceSelected')}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Add Device Dialog */}
      <Dialog open={openDialog === 'addDevice'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('device.addDeviceDialog')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('deviceSettings.deviceName', { name: '' }).replace(/:\s*$/, '')}
            fullWidth
            variant="outlined"
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
            placeholder={t('device.deviceNamePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
          <Button onClick={handleAddDeviceSubmit} variant="contained">{t('dashboard.addDevice')}</Button>
        </DialogActions>
      </Dialog>

      {/* Pending Invites Dialog */}
      <Dialog open={openDialog === 'invites'} onClose={() => setOpenDialog('')} maxWidth="md" fullWidth>
        <DialogTitle>{t('invites.pendingInvitationsTitle')}</DialogTitle>
        <DialogContent>
          {pendingInvites.length === 0 ? (
            <Typography color="textSecondary">{t('invites.noPendingInvitations')}</Typography>
          ) : (
            <List>
              {pendingInvites.map((invite) => (
                <ListItem key={invite.invite_id}>
                  <ListItemText
                    primary={`${invite.device_name} (${t('invites.roleAccess', { role: invite.role })})`}
                    secondary={`${t('invites.invitedBy', { inviter: invite.invited_by })} • ${t('invites.expires', { date: new Date(invite.expires_at).toLocaleString() })}`}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      handleAcceptInvite(invite.token);
                      setOpenDialog('');
                    }}
                  >
                    {t('invites.accept')}
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DashboardLayout;
